import {ProjectOtherPathologiesForm} from '@/features/projects/components/project-other-pathologies-form/ProjectOtherPathologiesForm'
import {ProjectPathologiesDetails} from '@/features/projects/components/project-pathologies-details/ProjectPathologiesDetails'
import {useGetProjectOtherPathologies} from '@/features/projects/queries/useGetProjectOtherPathologies'
import {CardInEditModeType} from '@/pages/project-details-diagnoses-facilities/ProjectDetailsDiagnosesFacilities'
import {useTranslation} from '@/translations/i18n'
import {Dispatch, SetStateAction, useCallback, useMemo} from 'react'

interface ProjectOtherPathologiesSectionProps {
    projectId: string
    editModeState: [CardInEditModeType, Dispatch<SetStateAction<CardInEditModeType>>]
}

export const ProjectOtherPathologiesSection = ({projectId, editModeState}: ProjectOtherPathologiesSectionProps) => {
    const {t} = useTranslation()
    const [cardInEditMode, setCardInEditMode] = editModeState

    const {
        data: otherPathologies,
        isLoading,
        isError
    } = useGetProjectOtherPathologies({
        urlParams: {projectId}
    })

    const handleCancel = useCallback(() => {
        setCardInEditMode(null)
    }, [setCardInEditMode])

    const handleEdit = useCallback(() => {
        setCardInEditMode('ProjectOtherPathologiesSection')
    }, [setCardInEditMode])

    const isActive = useMemo(() => cardInEditMode == 'ProjectOtherPathologiesSection', [cardInEditMode])
    const isDisabled = useMemo(
        () => !!(cardInEditMode && cardInEditMode != 'ProjectOtherPathologiesSection'),
        [cardInEditMode]
    )

    const otherPathologiesDefaultValues = useMemo(() => {
        return otherPathologies && otherPathologies?.length > 0
            ? otherPathologies.map(projectPathology => ({
                  diagnosis: projectPathology.pathology,
                  icd10Diagnosis: projectPathology.icd10Pathology
              }))
            : undefined
    }, [otherPathologies])

    if (isActive) {
        return (
            <ProjectOtherPathologiesForm
                projectId={projectId}
                defaultValues={otherPathologiesDefaultValues}
                isDisabled={isDisabled}
                isEditModeActive={isActive}
                handleCancel={handleCancel}
                handleEdit={handleEdit}
            />
        )
    }

    return (
        <ProjectPathologiesDetails
            pathologies={otherPathologies}
            isDisabled={isDisabled}
            isLoading={isLoading}
            isActive={isActive}
            isError={isError}
            title={t('projects:other_pathologies_card_title')}
            handleCancel={handleCancel}
            handleEdit={handleEdit}
        />
    )
}
